import { useLayoutEffect } from "@tanstack/react-router"
import { AnimatePresence, motion } from "framer-motion"
import { useRef, useState } from "react"
import { useFocusWithin, useHover, useObjectRef } from "react-aria"
import {
  Group,
  InputContext,
  InputProps,
  TextField as RACTextField,
  TextFieldProps,
  useContextProps,
} from "react-aria-components"
import { RefCallBack } from "react-hook-form"

import BaseInput from "@/globalComponents/atoms/BaseInput"
import { Button } from "@/globalComponents/atoms/Button"
import Icon from "@/globalComponents/atoms/Icon"
import Select from "@/globalComponents/molecules/Select"
import { cn } from "@/utilities/etc"
import useToggle from "@/utilities/hooks/useToggle"
import {
  CountryIdentifiers,
  CountryPhoneData,
  getCountriesWithPhoneData,
  getCountryPhoneData,
} from "@/utilities/localization"
import { normalizePhoneNumber } from "@/utilities/normalizers"
import {
  detectCountry,
  formatNationalNumber,
  getCallingCode,
} from "@/utilities/phone"

import styles from "./TextField.module.css"

interface PhoneWithCountryProps extends Omit<InputProps, "type"> {
  innerRef: RefCallBack
  defaultCountry?: CountryIdentifiers
  onCountryChange?: (country: CountryPhoneData) => void
  placeholder?: string
  disabled?: boolean
}

const TextField = (props: TextFieldProps) => {
  return (
    <RACTextField {...props} className={cn("field", props.className)}>
      {props.children}
    </RACTextField>
  )
}

const Input: React.FC<InputProps & { innerRef?: RefCallBack }> = (props) => {
  const ref = useObjectRef(props.innerRef)
  const [inputCTX] = useContextProps(props, ref, InputContext)
  return (
    <BaseInput {...inputCTX} className={cn("field--input", props.className)} />
  )
}

const Password: React.FC<
  Omit<InputProps, "type"> & { innerRef: RefCallBack }
> = (props) => {
  const [showPassword, togglePasswordVisibility] = useToggle(false)
  const [inputCTX] = useContextProps(props, props.innerRef, InputContext)
  return (
    <Group className={cn("field--input", styles.passwordFieldWrapper)}>
      <BaseInput
        {...props}
        type={showPassword ? "text" : "password"}
        placeholder="●●●●●●●●"
        className={cn(props.className)}
      />
      <AnimatePresence>
        {inputCTX.value && (
          <motion.div
            key={inputCTX.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0 } }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            className={styles.passwordToggle}
          >
            <Button
              onPress={togglePasswordVisibility}
              presentation="ghost"
              buttonType="primary"
            >
              <Icon name={showPassword ? "hiddenEye" : "eye"} />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </Group>
  )
}

const Phone: React.FC<Omit<InputProps, "type"> & { innerRef: RefCallBack }> = (
  props
) => {
  const [inputCTX] = useContextProps(props, props.innerRef, InputContext)

  return (
    <div className={cn("field--input", styles.phoneFieldWrapper)}>
      {/* <Icon
        name="flagUnitedStates"
        retainOriginalColors
        className={styles.flag}
      /> */}
      <BaseInput
        {...inputCTX}
        type="tel"
        value={normalizePhoneNumber(inputCTX.value?.toString() || "")}
        maxLength={17}
        inputMode="numeric"
        className={cn(inputCTX.className)}
      />
    </div>
  )
}

const PhoneWithCountrySelect: React.FC<PhoneWithCountryProps> = ({
  innerRef,
  defaultCountry = "US",
  onCountryChange,
  placeholder = "",
  disabled = false,
  ...props
}) => {
  const containerRef = useRef<HTMLInputElement>(null)
  const [inputProps, ref] = useContextProps(props, innerRef, InputContext)
  const [containerWidth, setContainerWidth] = useState(0)

  const [selectedCountry, setSelectedCountry] = useState<CountryPhoneData>(
    () => {
      const country = getCountryPhoneData(
        detectCountry(inputProps.value?.toString() || "", defaultCountry)
      )
      return country!
    }
  )

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        const width = entry.borderBoxSize.length
          ? entry.borderBoxSize[0].inlineSize
          : 0
        setContainerWidth(width)
      }
    })

    observer.observe(container)

    return () => observer.disconnect()
  }, [])

  const countries = getCountriesWithPhoneData()

  const getPhoneDigits = () => {
    const fullValue = inputProps.value?.toString() || ""
    if (!fullValue) return ""
    const allDigits = fullValue.replace(/\D/g, "")
    const callingCode = getCallingCode(selectedCountry.code)
    return allDigits.startsWith(callingCode)
      ? allDigits.slice(callingCode.length)
      : allDigits
  }

  const handleCountrySelect = (countryCode: CountryIdentifiers) => {
    const country = getCountryPhoneData(countryCode)
    if (!country) return

    setSelectedCountry(country)
    onCountryChange?.(country)
    const phoneDigits = getPhoneDigits()
    const newValue = `+${getCallingCode(country.code)}${phoneDigits}`

    if (inputProps.onChange && ref.current) {
      const event = new Event("change", { bubbles: true })
      Object.defineProperty(event, "target", {
        value: { ...ref.current, value: newValue },
        writable: false,
      })
      inputProps.onChange(event as any)
    }

    // Focus the input after country selection
    setTimeout(() => {
      ref.current?.focus()
    }, 0)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    const caret =
      e.target.selectionStart === null
        ? rawValue.length
        : e.target.selectionStart
    const inputType = (e.nativeEvent as InputEvent).inputType
    const prevDigits = getPhoneDigits()
    let phoneDigits = rawValue.replace(/\D/g, "")

    // Backspacing a formatting char (paren/space/dash) removes only that char,
    // leaving the digit count unchanged — the formatter would then re-insert it
    // and the field feels "stuck". Treat it as deleting the digit just before
    // the caret instead.
    if (
      inputType === "deleteContentBackward" &&
      phoneDigits.length === prevDigits.length
    ) {
      const digitsBeforeCaret = rawValue
        .slice(0, caret)
        .replace(/\D/g, "").length
      if (digitsBeforeCaret > 0) {
        phoneDigits =
          phoneDigits.slice(0, digitsBeforeCaret - 1) +
          phoneDigits.slice(digitsBeforeCaret)
      }
    }

    const formValue = `+${getCallingCode(selectedCountry.code)}${phoneDigits}`

    if (inputProps.onChange) {
      Object.defineProperty(e, "target", {
        value: { ...e.target, value: formValue },
        writable: false,
      })
      inputProps.onChange(e)
    }
  }

  return (
    <div
      className={cn("field--input", "input", styles.phoneWithCountryWrapper)}
      ref={containerRef}
    >
      <Select
        name="country"
        value={selectedCountry.code}
        onChange={handleCountrySelect}
        disabled={disabled}
        aria-label="Select country"
      >
        <Select.Value>
          {selectedCountry.iconName && (
            <Icon
              name={selectedCountry.iconName}
              slot="left"
              retainOriginalColors
              size="large"
            />
          )}
        </Select.Value>
        <Select.Popover
          style={
            containerWidth > 0
              ? { "--trigger-width": `${containerWidth}px` }
              : {}
          }
        >
          <Select.ListBox items={countries}>
            {(country) => (
              <Select.ListBoxItem
                key={country.code}
                id={country.code}
                textValue={country.name}
                className={styles.countryOption}
              >
                {country.iconName && (
                  <Icon
                    name={country.iconName}
                    retainOriginalColors
                    size="large"
                  />
                )}
                <span>{country.name}</span>
                <span>{country.dialCode}</span>
              </Select.ListBoxItem>
            )}
          </Select.ListBox>
        </Select.Popover>
      </Select>
      <div className={styles.countryCodeDisplay}>
        {selectedCountry.dialCode}
      </div>
      <BaseInput
        {...inputProps}
        innerRef={ref}
        type="tel"
        value={(() => {
          const phoneDigits = getPhoneDigits()
          const formattedValue = formatNationalNumber(
            phoneDigits,
            selectedCountry.code
          )
          return formattedValue ? `\u2009${formattedValue}` : ""
        })()}
        onChange={handleInputChange}
        placeholder={placeholder ? `\u2009${placeholder}` : ""}
        disabled={disabled}
        unstyled
        className="input--1"
        inputMode="tel"
        autoComplete="tel"
      />
    </div>
  )
}

// Will need to refactor this to accept end/start adornment

const AdornedInput: React.FC<
  InputProps & { innerRef: RefCallBack; adornment: string }
> = (props) => {
  const [inputProps] = useContextProps(props, props.innerRef, InputContext)
  const [isFocusWithin, setFocusWithin] = useState(false)
  const { hoverProps, isHovered } = useHover({})
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (isFocusWithin) => setFocusWithin(isFocusWithin),
  })

  return (
    <div
      {...focusWithinProps}
      {...hoverProps}
      data-readonly={inputProps.readOnly || undefined}
      data-invalid={inputProps["aria-invalid"] || undefined}
      data-focused={isFocusWithin || undefined}
      data-hovered={isHovered || undefined}
      className={cn("field--input")}
    >
      {props.adornment && <span>{props.adornment}</span>}
      <BaseInput {...inputProps} className={cn(props.className)} unstyled />
    </div>
  )
}

TextField.Input = Input
TextField.Password = Password
TextField.Phone = Phone
TextField.PhoneWithCountrySelect = PhoneWithCountrySelect
TextField.AdornedInput = AdornedInput

export default TextField
